import React, { useEffect, useState } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import { showAlert } from "store/reducers/alertReducer"
import { customBatchPrinting } from "store/actions/cardsAction"

const BatchPrintingModal = ({ isOpen, toggle, trigger, selectedBatch }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const cardsCount = Number(selectedBatch?.cards_count || 0)
  const metaData = selectedBatch?.meta_data || {}

  const [showReprintWarning, setShowReprintWarning] = useState(false)
  const [showContinueConfirm, setShowContinueConfirm] = useState(false)

  // Determine default values based on metaData
  const initialFrom = metaData?.to
    ? metaData.to === cardsCount
      ? 1
      : metaData.to + 1
    : 1

  const initialTo = cardsCount

  const validationSchema = Yup.object().shape({
    from: Yup.number()
      .required(t("Field is required"))
      .min(1, t("Minimum value is 1"))
      .max(cardsCount, t("Value must be within range")),
    to: Yup.number()
      .required(t("Field is required"))
      .min(Yup.ref("from"), t("Must be greater than or equal to 'from'"))
      .max(cardsCount, t("Value must be within range")),
  })

  const formik = useFormik({
    initialValues: {
      from: initialFrom,
      to: initialTo,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const payload = {
        from: Number(values.from),
        to: Number(values.to),
        id_card_batch_uuid: selectedBatch.uuid,
      }

      try {
        const response = await dispatch(customBatchPrinting(payload))

        const blob = response?.payload
        if (blob instanceof Blob) {
          const file = new Blob([blob], { type: "application/pdf" })
          const fileURL = URL.createObjectURL(file)
          window.open(fileURL, "_blank")
          dispatch(
            showAlert({
              text: t("PDF generated successfully"),
              type: "success",
            })
          )
          toggle()
          trigger()
        } else {
          dispatch(
            showAlert({ text: t("Invalid response format"), type: "error" })
          )
        }
      } catch (error) {
        dispatch(showAlert({ text: t("Unexpected error"), type: "error" }))
      } finally {
        setSubmitting(false)
      }
    },
  })

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
  } = formik

  // Show appropriate warnings on open
  useEffect(() => {
    if (isOpen && metaData?.to) {
      if (metaData.to === cardsCount) {
        setShowReprintWarning(true)
      } else if (metaData.to < cardsCount) {
        setShowContinueConfirm(true)
      }
    } else {
      setShowReprintWarning(false)
      setShowContinueConfirm(false)
    }
  }, [isOpen, metaData, cardsCount])

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>{t("Batch Printing")}</ModalHeader>
      <ModalBody>
        <div className="border rounded p-3">
          <h5>
            {t("Total Cards in Batch:")}{" "}{cardsCount}
          </h5>

          {showReprintWarning && (
            <div className="alert alert-warning fw-semibold">
              {t(
                "You have already printed all the ID cards. Do you still want to print again?"
              )}
            </div>
          )}

          {showContinueConfirm && (
            <div className="alert alert-info fw-semibold">
              {t("You have previously printed up to card")} {metaData.to}.{" "}
              {t("Are you sure you want to continue printing From")}{": "}
              {metaData.to + 1} {t("To")}{":"} {cardsCount}?
            </div>
          )}

          <div className="form_field mt-3">
            <label>
              {t("Add range of cards from:1 to:")}{" "}{cardsCount}
            </label>
            <div className="d-flex gap-3">
              <div className="w-100">
                <input
                  name="from"
                  type="number"
                  min="1"
                  max={cardsCount}
                  placeholder={t("From")}
                  className="form-control"
                  value={values.from}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.from && errors.from && (
                  <div className="text-danger fw-semibold mt-1">
                    {errors.from}
                  </div>
                )}
              </div>
              <div className="w-100">
                <input
                  name="to"
                  type="number"
                  min="1"
                  max={cardsCount}
                  placeholder={t("To")}
                  className="form-control"
                  value={values.to}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.to && errors.to && (
                  <div className="text-danger fw-semibold mt-1">
                    {errors.to}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="main_btn disabled-button fit_content px-4"
            onClick={toggle}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="main_btn fit_content px-4"
            onClick={handleSubmit}
            disabled={isSubmitting || !formik.isValid }
          >
            {t("Print")}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default BatchPrintingModal
